import * as React from 'react';
import styled, { css } from 'styled-components';

import { GreenTickIcon, InUseIcon } from '../../icons/Icons';
import { DbtLogo } from '../../icons/IntegrationLogos';
import { Card } from '../code-illustrations/SharedStyles';

import { DataConnectionName } from './DataConnectionName';

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
`;

const InUse = styled.div`
  display: flex;
  align-items: center;
  background: #efebff;
  border-radius: 2px;
  color: #3e277a;
  padding: 0 5px;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  height: 20px;
  line-height: 20px;
  margin-left: auto;
  gap: 4px;

  svg {
    color: #3e277a;
    opacity: 0.5;
  }
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #717a94;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  margin-top: 7px;
  line-height: initial;
`;

const TableInfo = styled.div`
  padding: 15px 15px 0;
  margin: 15px -15px 0;
  border-top: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
`;

const TableNameAndMisc = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.SMALL};

  span {
    margin-left: auto;
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    color: #717a94;
    font-weight: 400;
  }
`;

const DbtSection = styled.div`
  display: flex;
  align-items: stretch;
  height: 40px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
  border-radius: 2px;
  padding: 5px;
  margin-top: 10px;

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
    flex: 1 1 auto;
    gap: 5px;
    color: #717a94;
    font-size: ${({ theme }) => theme.fontSize.SMALL};

    &:first-child {
      text-transform: uppercase;
      font-weight: 700;
      font-size: 10px;

      > div {
        width: 16px;
        height: 16px;

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }

    &:last-child {
      border-right: none;
    }
  }
`;

const ColumnList = styled.div`
  padding-top: 7px;

  > div {
    display: flex;
    align-items: center;
    padding: 2px 0;
    color: #717a94;
    font-size: ${({ theme }) => theme.fontSize.SMALL};

    > div {
      flex: 1 1 auto;
      width: 50%;
    }
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background-color: ${({ theme }) => theme.appUI.borderColor.DEFAULT};
  flex: none;
`;

const ConnectionCard = styled(Card)<{ $small?: boolean }>`
  min-height: 60px;
  width: 100%;
  position: relative;
  z-index: 3;

  ${({ $small }) =>
    $small &&
    css`
      padding: 10px 12px;

      ${InUse} {
        border-radius: 1px;
        padding: 0 3px;
        font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
        height: 16px;
        line-height: 16px;
        gap: 2px;
      }

      ${Metadata} {
        gap: 5px;
        font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
        margin-top: 5px;
      }

      ${TableInfo} {
        padding: 10px 12px 0;
        margin: 12px -12px 0;
      }

      ${TableNameAndMisc} {
        font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
      }

      ${DbtSection} {
        margin-top: 5px;
        padding: 3px;
        height: 30px;

        span {
          font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};

          &:first-child {
            font-size: 8px;

            > div {
              width: 14px;
              height: 14px;

              svg {
                width: 14px;
                height: 14px;
              }
            }
          }
        }
      }

      ${ColumnList} {
        > div {
          font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
          line-height: 14px;
        }
      }
    `}
`;

interface DataConnectionCardProps {
  name: string;
  small?: boolean;
  withdbt?: boolean;
}

export const DataConnectionCard: React.ComponentType<DataConnectionCardProps> =
  React.memo(({ name, withdbt = false, small = false }) => {
    const connectionCards = (
      <>
        <ConnectionCard $small={small}>
          <ConnectionInfo>
            <DataConnectionName name={name} />
            <InUse>
              <InUseIcon />2 cells
            </InUse>
          </ConnectionInfo>
          <Metadata>
            <div>Atriedes, Inc.</div>
            <Divider />
            <div>Last updated: 3 min ago</div>
          </Metadata>
        </ConnectionCard>
        <ConnectionCard />
        <ConnectionCard />
      </>
    );

    const dbtmetadata = (
      <ConnectionCard $small={small}>
        <ConnectionInfo>
          <DataConnectionName name={name} />
          <InUse>
            <InUseIcon />2 cells
          </InUse>
        </ConnectionInfo>
        <Metadata>
          <div>Atriedes, Inc.</div>
          <Divider />
          <div>Last updated: 3 min ago</div>
        </Metadata>
        <TableInfo>
          <TableNameAndMisc>
            vehicle_destinations
            <span>16 cols, 192 rows</span>
          </TableNameAndMisc>
          <DbtSection>
            <span>
              <DbtLogo />
              Model
            </span>
            <span>2 min ago</span>
            <span>
              <GreenTickIcon /> Tests
            </span>
          </DbtSection>
          <ColumnList>
            <div>
              <div>VEHICLE_ID</div>
              <div>string</div>
            </div>
            <div>
              <div>MODEL</div>
              <div>string</div>
            </div>
            <div>
              <div>SPEED</div>
              <div>number</div>
            </div>
            <div>
              <div>BUILD_DATE</div>
              <div>datetime</div>
            </div>
          </ColumnList>
        </TableInfo>
      </ConnectionCard>
    );

    return !withdbt ? connectionCards : dbtmetadata;
  });
